import { Suspense, useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { resetPassword } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";

export const Route = createFileRoute("/_auth/reset-password")({
  validateSearch: z.object({
    token: z.string().optional(),
    email: z.string().email().optional(),
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  return (
    <Suspense fallback={null}>
      <ResetPasswordForm />
    </Suspense>
  );
}

function ResetPasswordForm() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">잘못된 링크</CardTitle>
          <CardDescription>
            재설정 토큰이 없거나 손상되었습니다. 비밀번호 찾기를 다시 시도해주세요.
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            비밀번호 찾기로 이동
          </Link>
        </CardFooter>
      </Card>
    );
  }

  const resetToken = token;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(resetToken, password);
      setDone(true);
      setTimeout(() => navigate({ to: "/login" }), 2000);
    } catch (err) {
      setError(createErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">비밀번호가 변경되었습니다</CardTitle>
          <CardDescription>곧 로그인 페이지로 이동합니다.</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-4">
          <CheckCircle2 className="size-12 text-emerald-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">새 비밀번호 설정</CardTitle>
        <CardDescription>새로 사용할 비밀번호를 입력해주세요.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">새 비밀번호</Label>
            <Input
              id="password"
              type="password"
              placeholder="최소 8자"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password-confirm">비밀번호 확인</Label>
            <Input
              id="password-confirm"
              type="password"
              placeholder="비밀번호를 다시 입력하세요"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>
          <Button type="submit" size="lg" disabled={submitting} className="mt-2">
            {submitting ? (
              <>
                <Loader2 className="mr-1 size-4 animate-spin" />
                변경 중...
              </>
            ) : (
              "비밀번호 변경"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
